import React, { useState } from 'react';
import {
  Button,
  TagIcon,
  Text,
  Tooltip,
  DirectionsIcon,
} from '@billda/design-system';
import { What3WordsLink } from '../../../../../shared/components/atoms/What3WordsLink';
import ConfirmDialog from '../../../../../shared/components/ui/ConfirmDialog';

interface SiteCardHeaderProps {
  name: string;
  slug?: string;
  what3WordsAddress?: string;
  latitude?: number | null;
  longitude?: number | null;
  /** When true, directions icon sits right next to the w3w address. When false (default), it's pushed to the far right. */
  compactDirections?: boolean;
  actions?: React.ReactNode;
}

export const SiteCardHeader: React.FC<SiteCardHeaderProps> = ({
  name,
  slug,
  what3WordsAddress,
  latitude,
  longitude,
  compactDirections = false,
  actions,
}) => {
  const [directionsDialogOpen, setDirectionsDialogOpen] = useState(false);

  const hasCoordinates = latitude != null && longitude != null;

  const handleOpenDirections = () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`,
      '_blank',
      'noopener,noreferrer'
    );
    setDirectionsDialogOpen(false);
  };

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        {/* Left side: Site name and location info */}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Text
            variant="h6"
            data-testid="site-name-header"
            className="truncate font-semibold"
          >
            {name}
          </Text>
          {slug && (
            <div className="flex items-center gap-1" data-testid="site-slug">
              <TagIcon
                className="size-4 text-content-secondary"
                aria-hidden="true"
              />
              <Text variant="body2" color="secondary">
                {slug}
              </Text>
            </div>
          )}
          {what3WordsAddress && (
            <div
              className={`flex items-center ${
                compactDirections ? 'gap-1' : 'justify-between'
              }`}
            >
              <What3WordsLink
                address={what3WordsAddress}
                testId="site-location"
              />
              {hasCoordinates && (
                <Tooltip content="Get Directions">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-content-accent"
                    data-testid="get-directions-button"
                    aria-label="Get Directions"
                    onClick={e => {
                      e.stopPropagation();
                      setDirectionsDialogOpen(true);
                    }}
                  >
                    <DirectionsIcon className="size-4" aria-hidden="true" />
                  </Button>
                </Tooltip>
              )}
            </div>
          )}
        </div>

        {/* Right side: Action buttons */}
        {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
      </div>

      {hasCoordinates && (
        <ConfirmDialog
          open={directionsDialogOpen}
          title="Open Directions"
          message="This will open Google Maps in a new tab. Do you want to continue?"
          confirmText="Open"
          cancelText="Cancel"
          onConfirm={handleOpenDirections}
          onCancel={() => setDirectionsDialogOpen(false)}
        />
      )}
    </>
  );
};
